<template>
  <SrpModal v-model:isVisible="isModalVisible" size="large" :isClosable="false" :isCloseButtonVisible="!isSaving">
    <template #content>
      <ChoosePhotosStep
        v-show="currentStep === WizardSteps.ChoosePhotos"
        ref="domRefChoosePhotosStep"
        :allPhotos="allPhotos"
        :customerId="props.customerId"
        @imageAddedToGallery="imageId => allPhotos.unshift(imageId)"
      />
      <GeneratePostStep v-show="currentStep === WizardSteps.GeneratePost" ref="domRefGeneratePostStep" @selectTemplate="validateAndGoToNextStep" />
      <SavePostStep :socialPostPreviews="socialPostPreviews" v-show="currentStep === WizardSteps.SavePost" ref="domRefSavePostStep" :isSaving="isSaving" />
    </template>

    <template #footer>
      <SrpButton v-if="currentStep === 1" color="gray" fill="outlined" style="margin-right: 12px" @click="isModalVisible = false" :isDisabled="isSaving"> Cancel </SrpButton>
      <SrpButton v-else color="gray" fill="outlined" style="margin-right: 12px" @click="currentStep -= 1" :isDisabled="isSaving"> Back </SrpButton>

      <SrpButton @click="validateAndGoToNextStep">
        {{ nextButtonName }}
        <template v-if="nextIconName" #icon><IconEmbedded :name="nextIconName" :size="19" /></template>
      </SrpButton>
    </template>
  </SrpModal>
</template>

<script setup lang="ts">
import { ref, onMounted, defineProps, nextTick, computed } from "vue";
import { useRoute, useRouter } from "vue-router";

// Components
import SrpModal from "@components/ui/SrpModal.vue";
import SrpButton from "@components/ui/SrpButton.vue";
import IconEmbedded from "@components/ui/IconEmbedded.vue";
import LoadingIcon from "@components/ui/LoadingIcon.vue";
// steps
import ChoosePhotosStep from "./ChoosePhotosStep.vue";
import GeneratePostStep from "./GeneratePostStep.vue";
import SavePostStep from "./SavePostStep.vue";
import axios from "axios";
import { SocialMediaPostData } from "@/contracts/shareStop";

// Router
const router = useRouter();

// DOM refs
const domRefChoosePhotosStep = ref<any>(null);
const domRefGeneratePostStep = ref<any>(null);
const domRefSavePostStep = ref<any>(null);

// Toggle modal ===============================================================
const isModalVisible = ref<boolean>(false);

// Change steps ===============================================================
enum WizardSteps {
  ChoosePhotos = 1,
  GeneratePost = 2,
  SavePost = 3,
}
const currentStep = ref<number>(WizardSteps.ChoosePhotos);

// Define props
const props = defineProps<{
  customerId: string;
}>();

// Define emits
const emit = defineEmits<{
  (e: "generatingStarted", postCount: number): void;
  (e: "generatingCompleted", socialPosts: SocialMediaPostData[]): void;
}>();

// Load Photos ==============================================================
const allPhotos = ref<string[]>([]);
onMounted(async () => {
  const photoResult = await axios.get(`${import.meta.env.VITE_API_URL}/photo-gallery/customer/${props.customerId}`);
  allPhotos.value = photoResult.data.photoIds;
});

// Computed properties for button text and icon ===============================================
const nextButtonName = computed(() => {
  if (currentStep.value === WizardSteps.ChoosePhotos) {
    return "Next";
  } else if (currentStep.value === WizardSteps.GeneratePost) {
    return "Generate!";
  } else if (currentStep.value === WizardSteps.SavePost) {
    return "Save";
  } else {
    return "Next";
  }
});

const nextIconName = computed(() => {
  return currentStep.value === WizardSteps.GeneratePost ? "ai_3" : null;
});

// Validate and go to next step ===============================================
async function validateAndGoToNextStep() {
  if (currentStep.value === WizardSteps.GeneratePost) {
    genSocialPosts();
    isModalVisible.value = false;
    return;
  }
  /* if (currentStep.value === WizardSteps.SelectCreator && !newCustomCollabWizardStore.isSelectCreatorFormValid) {
    domRefChoosePhotosStep.value.showValidationErrorNotes();
    return;
  } else if (currentStep.value === WizardSteps.GeneratePost && !newCustomCollabWizardStore.selectedTemplateId) {
    domRefGeneratePostStep.value.showValidationErrorNotes();
    return;
  }*/

  currentStep.value += 1;
}

const isSaving = ref<boolean>(false);
const socialPostPreviews = ref<Array<SocialMediaPostData>>([]);
async function genSocialPosts() {
  isSaving.value = true;
  socialPostPreviews.value = [];
  try {
    const postCount = domRefGeneratePostStep.value?.selectedPostCount || 1;
    console.warn("postCount", postCount);
    emit("generatingStarted", postCount);

    const url = `${import.meta.env.VITE_API_URL}/social-generator/${props.customerId}/selected-assets`;
    const payload = {
      assetIds: domRefChoosePhotosStep.value?.imageIdsSelected || [],
      userGuidance: domRefGeneratePostStep.value?.userGuidance,
      postCount: postCount,
      saveResults: true,
    };
    const { data } = await axios.post(url, payload);
    socialPostPreviews.value = data;
    emit("generatingCompleted", data);
  } finally {
    isSaving.value = false;
  }
}

// Open modal function for external use =======================================
async function openModal(selectedCreatorId = "") {
  isModalVisible.value = true;

  if (selectedCreatorId) {
    await nextTick();
    domRefChoosePhotosStep.value.setSelectedCreator(selectedCreatorId);
  }
}

// Define expose ==============================================================
defineExpose({
  openModal,
  closeModal: () => (isModalVisible.value = false),
});
</script>

<style scoped lang="scss"></style>
